import React, { useState, useMemo, useEffect } from "react";
import CTBIcon from "../../assets/icons/CTBIcon";
import { Link, NavLink, useLocation } from "react-router-dom";
import CreateIcon from "../../assets/icons/CreateIcon";
import CircleChevron from "../../assets/icons/CircleChevron";
import SignoutIcon from "../../assets/icons/SignoutIcon";
import { UserRole, logOut } from "../../utils/api/endpoints/user/credentials";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import { getUserLinks, NavLinkProps } from "./SideNavData";
import NavbarImg from "../../assets/icons/NavbarImg";
import { FiChevronDown } from "react-icons/fi";
import { getClientScopingStatus } from "../../utils/api/endpoints/questionnaire";
import ChecklistIcon from "../../assets/icons/ChecklistIcon";


const NavLinkItem = ({
  link,
  expanded,
  isActive,
  isMainDashboard
}: {
  link: NavLinkProps;
  expanded: boolean;
  isActive: boolean;
  isMainDashboard?: boolean;
}) => (
  <NavLink
    to={link.link}
    end={link.end}
    className={({ isActive: navIsActive }) =>
      `flex items-center gap-3 transition ${navIsActive || isActive
        ? "bg-[#0B2A1E] text-[#4ADE80] border border-[#14532D]"
        : "hover:bg-[#0B1A4A] text-[#D2D1D1]"
      } ${expanded ? "h-10 w-full rounded-[8px] px-3" : "h-10 w-10 rounded-lg justify-center"} ${isMainDashboard ? "mt-1 mb-4" : ""}`
    }
  >
    <link.icon className={`stroke-current ${isMainDashboard ? "h-5 w-5" : "h-5 w-5"}`} />
    {expanded && (
      <span className={`font-medium text-inherit ${isMainDashboard ? "text-sm" : "text-sm"}`}>{link.label} {link.is_beta && "(Beta)"}</span>
    )}
  </NavLink>
);

const NestedNavItem = ({
  link,
  expanded
}: {
  link: NavLinkProps;
  expanded: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // Check if any child is active
  const isChildActive = link.children?.some(child => location.pathname.startsWith(child.link)) || false;
  const isParentActive = location.pathname === link.link;

  return (
    <div>
      {/* Split button: NavLink for navigation + toggle button for dropdown */}
      <div className={`flex items-center gap-1 transition ${isParentActive || isChildActive
        ? "bg-[#0B2A1E] text-[#4ADE80] border border-[#14532D] rounded-[8px]"
        : "text-[#D2D1D1]"
        } ${expanded ? "" : "justify-center"}`}>

        {/* Main navigation link */}
        <NavLink
          to={link.link}
          end={link.end}
          className={({ isActive }) =>
            `flex items-center gap-3 transition flex-1 ${isActive
              ? "text-[#4ADE80]"
              : "hover:bg-[#0B1A4A] text-inherit"
            } ${expanded ? "h-10 rounded-l-[8px] px-3" : "h-10 w-10 rounded-lg justify-center"}`
          }
        >
          <link.icon className="h-5 w-5 stroke-current" />
          {expanded && (
            <span className="text-sm font-medium text-inherit">{link.label}</span>
          )}
        </NavLink>

        {/* Dropdown toggle button */}
        {expanded && link.children && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
            className="h-10 px-2 hover:bg-[#0B1A4A]/50 rounded-r-[8px] transition"
            aria-label="Toggle submenu"
          >
            <FiChevronDown
              className={`h-4 w-4 transition-transform text-inherit ${isOpen ? "rotate-180" : ""}`}
            />
          </button>
        )}
      </div>

      {/* Submenu items */}
      {expanded && isOpen && link.children && (
        <ul className="ml-5 mt-1 space-y-1 border-l border-[#14532D]/30 pl-2">
          {link.children.map((child, idx) => (
            <li key={`${child.label}-${idx}`}>
              <NavLink
                to={child.link}
                end={child.end}
                className={({ isActive }) =>
                  `flex items-center gap-2 transition text-xs ${isActive
                    ? "text-[#4ADE80] font-medium"
                    : "hover:text-[#4ADE80] text-[#9CA3AF]"
                  } h-8 rounded-r-[4px] py-1`
                }
              >
                <child.icon className="h-3.5 w-3.5 stroke-current" />
                <span className="text-inherit">{child.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const SideNav = ({
  className,
  expanded = true,
  toggleExpanded = () => { }
}: {
  className?: string;
  expanded?: boolean;
  toggleExpanded?: () => void;
}) => {
  const { role } = useUserCredentials();
  const [isIntakeMode, setIsIntakeMode] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (role === UserRole.BUSINESS) {
      getClientScopingStatus()
        .then(res => {
          if (res.locked) {
            setIsIntakeMode(true);
          } else {
            setIsIntakeMode(false);
          }
        })
        .catch(err => {
          console.error("Error checking scoping status in SideNav:", err);
          setIsIntakeMode(false);
        });
    } else {
      setIsIntakeMode(false);
    }
  }, [role]);

  const sections = useMemo(() => {
    if (isIntakeMode) {
      return [
        {
          name: "Operations",
          links: [
            {
              label: "Scope Intake",
              icon: ChecklistIcon,
              link: "/dashboard",
              end: true
            }
          ] as NavLinkProps[]
        }
      ];
    }

    const allLinks = role ? getUserLinks(role) : [];

    // Categorize links
    const sectionConfig: Record<string, string[]> = {
      "Overview": ["Dashboard"],
      "Operations": ["Companies", "Retests", "Checklists", "My Checklists", "Assets", "Programs", "Credential Vault", "Users", "Delegations"],
      "Reporting": ["Reports", "My Reports", "Pentest Reports"],
      "System": ["Profile", "Settings"]
    };

    const categorized: { name: string; links: NavLinkProps[] }[] = [
      { name: "Overview", links: [] },
      { name: "Operations", links: [] },
      { name: "Reporting", links: [] },
      { name: "System", links: [] }
    ];

    allLinks.forEach(link => {
      let found = false;
      for (const sectionName in sectionConfig) {
        if (sectionConfig[sectionName].includes(link.label)) {
          const sectionIndex = categorized.findIndex(s => s.name === sectionName);
          if (sectionIndex !== -1) {
            categorized[sectionIndex].links.push(link);
            found = true;
            break;
          }
        }
      }
      // If not found in config, put in Operations by default or handle otherwise
      if (!found) {
        // Fallback for any potentially missing links, maybe put in Operations or a separate "Other"
        const opsIndex = categorized.findIndex(s => s.name === "Operations");
        if (opsIndex !== -1) categorized[opsIndex].links.push(link);
      }
    });

    // Sort links within each section match the config order
    categorized.forEach(cat => {
      const order = sectionConfig[cat.name] || [];
      if (order.length > 0) {
        cat.links.sort((a, b) => {
          const indexA = order.indexOf(a.label);
          const indexB = order.indexOf(b.label);
          // If both are in config, sort by index
          if (indexA !== -1 && indexB !== -1) return indexA - indexB;
          // If one is not in config (fallback items), put them at the end
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return 0;
        });
      }
    });

    return categorized.filter(s => s.links.length > 0);
  }, [role, isIntakeMode]);

  return (
    <nav
      className={`relative z-40 flex h-full flex-col ${className}`}
      style={{ backgroundColor: "#010D2C" }}
    >
      <div className={`relative flex items-center ${expanded ? "px-6 py-6" : "justify-center py-4"}`}>
        {expanded ? (
          <h1 className="text-xl font-bold text-white whitespace-nowrap">Capture The Bug</h1>
        ) : (
          <NavbarImg className="h-10 w-auto" />
        )}
      </div>

      <div className={`flex w-full flex-grow flex-col px-4 overflow-y-auto ${expanded ? "" : "items-center"}`}>
        {sections.map((section, sectionIdx) => (
          <div key={section.name} className={`flex flex-col ${sectionIdx > 0 ? "mt-6" : ""}`}>
            {/* Section Header - Only show if expanded and not Overview (Overview usually just has Dashboard button) */}
            {expanded && section.name !== "Overview" && (
              <h3 className="mb-2 text-xs font-semibold text-slate-500 uppercase tracking-wider px-1">
                {section.name}
              </h3>
            )}

            <ul className="space-y-1">
              {section.links.map((link, linkIdx) => (
                <li key={`${link.label}-${linkIdx}`}>
                  {link.label === "Dashboard" ? (
                    <NavLinkItem
                      link={{ ...link, label: "Main Dashboard" }}
                      expanded={expanded}
                      isActive={false} // logic handled inside
                      isMainDashboard={true}
                    />
                  ) : link.children ? (
                    <NestedNavItem link={link} expanded={expanded} />
                  ) : (
                    <NavLinkItem
                      link={link}
                      expanded={expanded}
                      isActive={
                        link.label === "Scope Intake" ||
                        (link.link === "/dashboard/sales/intakes" && location.pathname.startsWith("/dashboard/sales/scoping/"))
                      }
                    />
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Sign out section */}
      <div className="w-full px-4 pt-4 pb-6 mt-auto">
        <button
          className={`flex h-10 w-full items-center gap-3 rounded-lg transition hover:bg-[#0B1A4A] text-[#D2D1D1] hover:text-white ${expanded ? "px-3" : "justify-center px-0"
            }`}
          onClick={() => logOut()}
        >
          <SignoutIcon className="h-5 w-5 stroke-current" />
          {expanded && <span className="text-sm font-medium">Sign out</span>}
        </button>
      </div>

      <button
        aria-label="Toggle sidebar"
        className={`absolute top-8 ${expanded ? "-right-3" : "left-16"
          } rounded-full bg-white text-slate-600 hover:text-slate-900 shadow-md p-1.5 border border-slate-100 hidden md:flex z-50`}
        onClick={toggleExpanded}
      >
        <CircleChevron
          className={`h-4 w-4 transition-transform ${expanded ? "" : "-rotate-180"
            }`}
        />
      </button>
    </nav>
  );
};

export default SideNav;
